import { Link } from "@tanstack/react-router";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { statusIntent } from "@/design";
import type { IntentName } from "@/design";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { SectionCard } from "@/shared/components/SectionCard";

import { useConnections } from "../api/use-connections";
import { useFulfillment } from "../api/use-fulfillment";
import { usePushState } from "../api/use-push-state";
import { RECON_MARKETPLACES, useReconGaps } from "../api/use-recon-gaps";
import { formatAge } from "../lib/age";

import { LiveDot } from "./LiveDot";

const TH_COL = "px-5 py-3 text-left";
const TD = "px-5 py-3.5 align-middle text-sm";
const ROW = "border-b border-[var(--row-divider)] last:border-b-0";

/** Refresh-token warning window, mirrors the BE's default (see
 * `ConnectionStatus.warning`'s doc in schema.d.ts). */
const TOKEN_WARNING_DAYS = 7;
const MS_PER_DAY = 86_400_000;

const TILE_CLASS: Record<IntentName, string> = {
  success: "bg-[var(--intent-success-bg)] text-[var(--intent-success-fg)]",
  warning: "bg-[var(--intent-warning-bg)] text-[var(--intent-warning-fg)]",
  danger: "bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]",
  info: "bg-[var(--intent-info-bg)] text-[var(--intent-info-fg)]",
  neutral: "bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]",
};

/** "—" with a title, for any cell whose source errored or has no row for
 * that marketplace — prefer a dash over a crash. */
function DashCell({ title }: { title?: string }): JSX.Element {
  return (
    <span title={title} className="text-[var(--text-subtle)]">
      —
    </span>
  );
}

/** A status tile: tinted block with a (pulsing, when healthy) dot and a
 * short label — the SynDi-style "green box = fine" read at a glance. */
function Tile({ intent, children }: { intent: IntentName; children: ReactNode }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-[10px] px-3 py-1.5 text-xs font-semibold ${TILE_CLASS[intent]}`}
    >
      <LiveDot intent={intent} />
      {children}
    </span>
  );
}

function ReconnectLink({ label }: { label: string }): JSX.Element {
  return (
    <Link
      to="/settings/marketplace-connections"
      className="text-xs font-semibold text-[var(--brand-600)] hover:underline"
    >
      {label}
    </Link>
  );
}

/** "HH:MM" in the viewer's local time — matches every other time on this
 * page (stuck-picked, notification-time). */
function formatTimeLocal(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function RowLabel({ label, hint }: { label: string; hint?: string }): JSX.Element {
  return (
    <th className={`${TH_COL} w-[260px] font-normal`} scope="row">
      <div className="text-sm font-semibold text-[var(--text-primary)]">{label}</div>
      {hint !== undefined && <div className="text-xs text-[var(--text-subtle)]">{hint}</div>}
    </th>
  );
}

/**
 * Marketplace health: one column per marketplace, one row each for the
 * connection token, orders pulled in, stock sync mode, and open recon gaps.
 *
 * Each row reads its own query independently so one source failing never
 * blanks the others — the recon row is already isolated per marketplace by
 * `useReconGaps`'s `useQueries` fan-out.
 */
export function PipelineTable(): JSX.Element {
  const { t } = useTranslation("dashboard");
  const now = new Date();

  const connections = useConnections();
  const fulfillment = useFulfillment();
  const pushState = usePushState();
  const reconGaps = useReconGaps();

  const tAge = (key: string, options?: Record<string, unknown>): string =>
    options === undefined ? t(key) : t(key, options);
  const checkedAt =
    fulfillment.dataUpdatedAt > 0 ? new Date(fulfillment.dataUpdatedAt).toISOString() : null;

  return (
    <SectionCard
      title={t("dashboard:pipeline.title")}
      caption={t("dashboard:pipeline.checked", { age: formatAge(checkedAt, now, tAge) })}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="pipeline-table">
          <thead>
            <tr className="border-b border-[var(--premium-border)]">
              <th className={TH_COL} />
              {RECON_MARKETPLACES.map((mkt) => (
                <th key={mkt} className={TH_COL} scope="col">
                  <MarketplaceChip marketplace={mkt} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className={ROW}>
              <RowLabel
                label={t("dashboard:pipeline.token")}
                hint={t("dashboard:pipeline.token_hint")}
              />
              {RECON_MARKETPLACES.map((mkt) => (
                <td key={mkt} className={TD} data-testid={`pipeline-token-${mkt}`}>
                  <TokenCell
                    mkt={mkt}
                    connections={connections.data}
                    isError={connections.isError}
                    now={now}
                  />
                </td>
              ))}
            </tr>

            <tr className={ROW}>
              <RowLabel
                label={t("dashboard:pipeline.ingest")}
                hint={t("dashboard:pipeline.ingest_hint")}
              />
              {RECON_MARKETPLACES.map((mkt) => (
                <td key={mkt} className={TD} data-testid={`pipeline-ingest-${mkt}`}>
                  <IngestCell
                    mkt={mkt}
                    ingest={fulfillment.data?.ingest}
                    isError={fulfillment.isError}
                    now={now}
                  />
                </td>
              ))}
            </tr>

            <tr className={ROW}>
              <RowLabel
                label={t("dashboard:pipeline.stock_push")}
                hint={t("dashboard:pipeline.stock_push_hint")}
              />
              {RECON_MARKETPLACES.map((mkt) => (
                <td key={mkt} className={TD} data-testid={`pipeline-stock-push-${mkt}`}>
                  <StockPushCell mkt={mkt} pushState={pushState.data} isError={pushState.isError} />
                </td>
              ))}
            </tr>

            <tr className={ROW}>
              <RowLabel
                label={t("dashboard:pipeline.recon_gaps")}
                hint={t("dashboard:pipeline.recon_gaps_hint")}
              />
              {reconGaps.map((result, i) => (
                <td
                  key={RECON_MARKETPLACES[i]}
                  className={TD}
                  data-testid={`pipeline-recon-${RECON_MARKETPLACES[i]}`}
                >
                  {result.isError ? (
                    <DashCell title="error" />
                  ) : (
                    <ReconCell open={result.data?.open} />
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

type ReconMarketplace = (typeof RECON_MARKETPLACES)[number];
type ConnectionRow = NonNullable<ReturnType<typeof useConnections>["data"]>[number];
type IngestRow = NonNullable<
  NonNullable<ReturnType<typeof useFulfillment>["data"]>["ingest"]
>[number];
type PushStateRow = NonNullable<ReturnType<typeof usePushState>["data"]>[number];

function ReconCell({ open }: { open: number | undefined }): JSX.Element {
  if (open === undefined) return <DashCell />;
  return (
    <span
      className={
        open > 0
          ? "font-bold tabular-nums text-[var(--intent-danger-fg)]"
          : "tabular-nums text-[var(--text-subtle)]"
      }
    >
      {open.toLocaleString()}
    </span>
  );
}

function TokenCell({
  mkt,
  connections,
  isError,
  now,
}: {
  mkt: ReconMarketplace;
  connections: ConnectionRow[] | undefined;
  isError: boolean;
  now: Date;
}): JSX.Element {
  const { t } = useTranslation("dashboard");
  if (isError || connections === undefined) return <DashCell title="error" />;

  // Prefer the active row for this marketplace; fall back to whatever row
  // (e.g. the synthesised not_connected entry) names it.
  const row =
    connections.find((c) => c.marketplace === mkt && c.status === "active") ??
    connections.find((c) => c.marketplace === mkt);
  if (row === undefined) return <DashCell title="error" />;

  const expiresAt = row.refresh_expires_at;
  // Only an *active* connection with an expiry still in the future can be
  // "expiring soon" — an already-expired token falls through to danger.
  const expiringSoon =
    row.status === "active" &&
    expiresAt != null &&
    new Date(expiresAt).getTime() > now.getTime() &&
    (row.warning === "refresh_token_expiring_soon" ||
      (new Date(expiresAt).getTime() - now.getTime()) / MS_PER_DAY <= TOKEN_WARNING_DAYS);

  // ponytail: connected / expiring / not-connected is this cell's own derived
  // 3-state — statusIntent()'s `marketplace_connection` domain has no
  // "expiring soon" code and colors disconnected neutral, which this row
  // must show as danger. Mapping stays inside IntentName — no invented color.
  if (expiringSoon) {
    const days =
      expiresAt == null
        ? 0
        : Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / MS_PER_DAY));
    return (
      <div className="flex items-center gap-3">
        <Tile intent="warning">{t("dashboard:pipeline.expiring", { days })}</Tile>
        <ReconnectLink label={t("dashboard:pipeline.reconnect")} />
      </div>
    );
  }
  if (row.status === "active") {
    return (
      <Tile intent={statusIntent("marketplace_connection", "active")}>
        {t("dashboard:pipeline.connected")}
      </Tile>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <Tile intent="danger">{t("dashboard:pipeline.not_connected")}</Tile>
      <ReconnectLink label={t("dashboard:pipeline.reconnect")} />
    </div>
  );
}

function IngestCell({
  mkt,
  ingest,
  isError,
  now,
}: {
  mkt: ReconMarketplace;
  ingest: IngestRow[] | undefined;
  isError: boolean;
  now: Date;
}): JSX.Element {
  const { t } = useTranslation("dashboard");
  if (isError || ingest === undefined) return <DashCell title="error" />;
  const row = ingest.find((i) => i.marketplace === mkt);
  if (row === undefined || row.last_fetched_at === null) return <DashCell />;

  const tAge = (key: string, options?: Record<string, unknown>): string =>
    options === undefined ? t(key) : t(key, options);
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-semibold text-[var(--text-primary)]">
        {formatAge(row.last_fetched_at, now, tAge)}
      </span>{" "}
      <span className="text-xs text-[var(--text-subtle)]">
        {t("dashboard:pipeline.ingest_today", { n: row.today_count.toLocaleString() })}
      </span>
    </span>
  );
}

function StockPushCell({
  mkt,
  pushState,
  isError,
}: {
  mkt: ReconMarketplace;
  pushState: PushStateRow[] | undefined;
  isError: boolean;
}): JSX.Element {
  const { t } = useTranslation("dashboard");
  if (isError || pushState === undefined) return <DashCell title="error" />;
  const row = pushState.find((p) => p.marketplace === mkt);
  if (row === undefined) return <DashCell />;

  // ponytail: "paused" overrides mode and has no push_mode taxonomy code —
  // the one literal intent left in this cell. live/shadow/off go through
  // statusIntent("push_mode", …).
  if (row.paused_at !== null) {
    return (
      <Link to="/inventory" search={{ search: "", offset: 0 }} className="hover:underline">
        <Tile intent="danger">
          {t("dashboard:pipeline.paused", { time: formatTimeLocal(row.paused_at) })}
        </Tile>
      </Link>
    );
  }
  const mode = row.mode === "live" || row.mode === "shadow" ? row.mode : "off";
  return <Tile intent={statusIntent("push_mode", mode)}>{t(`dashboard:pipeline.${mode}`)}</Tile>;
}
